import { useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { CheckIcon, UserMinusIcon, UserPlusIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { Input } from "simplydo/react/ui/input";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Pagination } from "simplydo/react/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

type GroupMembershipsMap = Record<string, string[]>;

const parseGroupMemberships = (
  memberships: OpenAPI.GET<"/groups/memberships">["response"]["memberships"],
): GroupMembershipsMap => {
  const parsed: GroupMembershipsMap = {};
  Object.entries(memberships).forEach(([groupId, value]) => {
    const members = value as boolean | string[];
    if (Array.isArray(members)) {
      parsed[groupId] = members;
    }
  });
  return parsed;
};

type GroupUserManagementModalProps = { users: string[]; onClose: () => void };

export const GroupUserManagementModal = ({
  users,
  onClose,
}: GroupUserManagementModalProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const limitPerPage = 10;
  const theme = useTheme();

  const [groupMemberships, setGroupMemberships] = useState<GroupMembershipsMap>(
    {},
  );
  useEffect(() => {
    let mounted = true;

    api.groups.getMemberships(
      ({ memberships }) => {
        if (!mounted) {
          return;
        }
        setGroupMemberships(parseGroupMemberships(memberships));
      },
      () => {
        if (!mounted) {
          return;
        }
        setGroupMemberships({});
      },
    );

    return () => {
      mounted = false;
    };
  }, []);

  if (
    !util.hasPermission(
      user,
      "org.addToGroupsDirectly",
      user?.ownerOrganisation?._id,
    )
  ) {
    return null;
  }

  const groupsWithPermissions =
    (util.hasPermission(user, "super.viewDashboard")
      ? user?.groups.filter((g) =>
          g.name.toLowerCase().includes(query.toLowerCase()),
        )
      : user?.groups.filter(
          (g) =>
            util.hasPermission(user, "group.editSettings", g._id) &&
            util.hasPermission(user, "group.listUsers", g._id) &&
            g.name.toLowerCase().includes(query.toLowerCase()),
        )) ?? [];
  const groupsOnPage = groupsWithPermissions.slice(
    (page - 1) * limitPerPage,
    page * limitPerPage,
  );

  return (
    <Modal open={users && users.length > 0} onClose={onClose}>
      <ModalHeader>Add to one of my {t("generic.groups")}</ModalHeader>
      <ModalContent scrolling>
        <p>
          Choose {t("generic.groupWithArticle")} to add the selected{" "}
          {util.pluralise(users.length, "user", "users", false)} to. This list
          only shows you the {t("generic.groups")} where you can manage users.
        </p>

        {query || groupsWithPermissions ? (
          <>
            <Input
              placeholder={`Search for ${t("generic.groupWithArticle")}`}

              value={query}
              onChange={(_e, { value }) => setQuery(value)}
            />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell></TableHeaderCell>
                  <TableHeaderCell>
                    {t("common:capitalise", { key: "generic.group" })}
                  </TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupsOnPage.map((group) => {
                  const allUsersInGroup = users.every((joinUserId) =>
                    groupMemberships[group._id]?.includes(joinUserId),
                  );

                  return (
                    <TableRow key={group._id}>
                      <TableCell>
                        <Image
                          src={util.groupCoverImage(
                            group as OpenAPI.Schemas["Group"],
                          )}
                          width={40}
                          height={40}
                          style={{
                            background: theme.primaryColour,
                            objectFit: "contain",
                            borderRadius: 5,
                          }}
                        />
                      </TableCell>
                      <TableCell>{group.name}</TableCell>
                      <TableCell>
                        <Button
                          color={allUsersInGroup ? "red" : "green"}
                          size="small"
                          onClick={() => {
                            const func = allUsersInGroup
                              ? api.groups.bulkLeave
                              : api.groups.bulkJoin;
                            func(
                              group._id,
                              { users },
                              () => {
                                toast.success(
                                  allUsersInGroup
                                    ? `Removed ${util.pluralise(users.length, "user", "users")} from ${group.name}`
                                    : `Added ${util.pluralise(users.length, "user", "users")} to ${group.name}`,
                                );
                                setGroupMemberships({
                                  ...groupMemberships,
                                  [group._id]: allUsersInGroup
                                    ? (
                                        groupMemberships[group._id] ?? []
                                      ).filter(
                                        (memberId) => !users.includes(memberId),
                                      )
                                    : [
                                        ...(groupMemberships[group._id] || []),
                                        ...users,
                                      ],
                                });
                              },
                              () => {},
                            );
                          }}
                        >
                          <Icon
                            icon={
                              allUsersInGroup ? UserMinusIcon : UserPlusIcon
                            }
                          />
                          {allUsersInGroup
                            ? `Remove ${util.pluralise(users.length, "user", "users")}`
                            : `Add  ${util.pluralise(users.length, "user", "users")}`}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {groupsWithPermissions.length / limitPerPage > 1 ? (
              <Pagination
                activePage={page}
                onPageChange={(_e, { activePage }) =>
                  setPage(activePage as number)
                }
                totalPages={Math.ceil(
                  groupsWithPermissions.length / limitPerPage,
                )}
              />
            ) : null}
          </>
        ) : (
          <Message color="blue">
            You currently do not have the required permissions to add users
            directly to any of your {t("generic.groups")}. To add users you need
            to have the &quot;Edit settings&quot; and &quot;List users&quot;
            permissions in your {t("generic.group")} role.
          </Message>
        )}
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose} color="primary">
          <Icon icon={CheckIcon} />
          Done
        </Button>
      </ModalActions>
    </Modal>
  );
};
